import React from 'react';
import { InfoIcon, CheckCircleIcon, ArrowUpCircleIcon } from 'lucide-react';
import { oohData } from '../utils/oohData';
const getCategoryBorder = (category: string) => {
  if (category === 'Visual Capture') return 'border-l-blue-400';
  if (category === 'Shape & Edge') return 'border-l-purple-400';
  if (category === 'Biological Attention') return 'border-l-pink-400';
  if (category === 'Structural & Cognitive') return 'border-l-orange-400';
  return 'border-l-gray-300';
};
type Driver = (typeof oohData.drivers)[number];
const DriverTitle: React.FC<{
  name: string;
  definition?: string;
}> = ({
  name,
  definition
}) => <div className="flex items-center space-x-2" data-id="element-104">
    <h4 className="font-bold text-gray-900 text-sm tracking-wide" data-id="element-105">{name}</h4>
    {definition && <span className="relative group inline-flex" data-export-hide data-id="element-106">
        <button type="button" aria-label={`What ${name} measures`} className="text-gray-300 hover:text-[#1E90FF] focus:text-[#1E90FF] focus:outline-none transition-colors" data-id="element-107">
          <InfoIcon className="h-3.5 w-3.5" data-id="element-108" />
        </button>
        <span role="tooltip" className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 w-60 -translate-x-1/2 rounded-lg bg-gray-900 px-3 py-2 text-xs font-normal leading-relaxed text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100" data-id="element-109">
          {definition}
        </span>
      </span>}
  </div>;
export const DriverAnalysis: React.FC = () => {
  const {
    drivers,
    driverDefinitions
  } = oohData;
  const notApplicable = drivers.filter(d => d.verdict === 'neutral_not_applicable');
  const improvable = drivers.filter(d => d.verdict !== 'neutral_not_applicable' && d.improvement);
  const passed = drivers.filter(d => d.verdict !== 'neutral_not_applicable' && !d.improvement);
  return <div className="bg-white p-6 rounded-xl shadow-sm" data-id="element-110">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3" data-id="element-111">
        <h2 className="text-lg font-medium text-gray-800" data-id="element-112">
          Attention Driver Analysis
        </h2>
        <div className="flex items-center gap-4 text-xs text-gray-500" data-id="element-113">
          <span className="inline-flex items-center gap-1.5" data-id="element-114">
            <span className="export-dot-down w-2 h-2 rounded-full bg-green-500" data-id="element-115" />
            {passed.length} passed
          </span>
          <span className="inline-flex items-center gap-1.5" data-id="element-116">
            <span className="export-dot-down w-2 h-2 rounded-full bg-amber-500" data-id="element-117" />
            {improvable.length} to improve
          </span>
        </div>
      </div>

      {/* Opportunities to improve — most important, shown first */}
      <section className="mb-8" data-id="element-118">
        <div className="flex items-center space-x-2 mb-4" data-id="element-119">
          <ArrowUpCircleIcon className="export-icon-down h-5 w-5 text-amber-600" data-id="element-120" />
          <h3 className="text-sm font-semibold text-amber-800 uppercase tracking-wide" data-id="element-121">
            Opportunities to improve
          </h3>
        </div>
        <div className="space-y-4" data-id="element-122">
          {improvable.map((driver: Driver) => <div key={driver.name} className={`border border-gray-100 rounded-lg p-4 border-l-4 ${getCategoryBorder(driver.category)} bg-white hover:shadow-md transition-shadow`} data-id="element-123">
              <div className="flex flex-col md:flex-row md:items-start gap-4" data-id="element-124">
                <div className="md:w-1/4 flex flex-col space-y-1.5" data-id="element-125">
                  <DriverTitle name={driver.name} definition={driverDefinitions[driver.name]} data-id="element-126" />
                  <span className="text-xs text-gray-500 font-medium" data-id="element-127">
                    {driver.category}
                  </span>
                </div>
                <div className="md:w-3/4 space-y-3" data-id="element-128">
                  <p className="text-sm text-gray-700 leading-relaxed" data-id="element-129">
                    {driver.observation}
                  </p>
                  <div className="bg-amber-50 border border-amber-100 rounded-md p-3 flex items-start space-x-3" data-id="element-130">
                    <div className="export-dot-down w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" data-id="element-131" />
                    <p className="text-sm text-amber-900 leading-relaxed" data-id="element-132">
                      <span className="font-semibold mr-1" data-id="element-133">Fix:</span>
                      {driver.improvement}
                    </p>
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </section>

      {/* Working well / passed */}
      <section className="mb-8" data-id="element-134">
        <div className="flex items-center space-x-2 mb-4" data-id="element-135">
          <CheckCircleIcon className="export-icon-down h-5 w-5 text-green-600" data-id="element-136" />
          <h3 className="text-sm font-semibold text-green-800 uppercase tracking-wide" data-id="element-137">
            Working well
          </h3>
        </div>
        <div className="space-y-4" data-id="element-138">
          {passed.map((driver: Driver) => <div key={driver.name} className={`border border-gray-100 rounded-lg p-4 border-l-4 ${getCategoryBorder(driver.category)} bg-white hover:shadow-md transition-shadow`} data-id="element-139">
              <div className="flex flex-col md:flex-row md:items-start gap-4" data-id="element-140">
                <div className="md:w-1/4 flex flex-col space-y-1.5" data-id="element-141">
                  <DriverTitle name={driver.name} definition={driverDefinitions[driver.name]} data-id="element-142" />
                  <span className="text-xs text-gray-500 font-medium" data-id="element-143">
                    {driver.category}
                  </span>
                </div>
                <div className="md:w-3/4" data-id="element-144">
                  <p className="text-sm text-gray-700 leading-relaxed" data-id="element-145">
                    {driver.observation}
                  </p>
                </div>
              </div>
            </div>)}
        </div>
      </section>

      {/* Not applicable — kept deliberately vague, no per-driver breakdown */}
      {notApplicable.length > 0 && <div className="bg-gray-50 border border-gray-100 rounded-lg p-4" data-id="element-146">
          <p className="text-sm text-gray-500 leading-relaxed" data-id="element-147">
            <span className="font-medium text-gray-600" data-id="element-148">
              Several further drivers from the Attention Blueprint
            </span>{' '}
            were assessed but are not applicable to this execution — largely
            biological attention cues that are not present in the creative.
          </p>
        </div>}
    </div>;
};